import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  getDashboardStats,
  getUserGrowthData,
  getRevenueData,
  getTopTeachers,
  getDateRange
} from './adminService';
import { getMultipleTeacherRatings } from './bookingService';
import {
  Users,
  GraduationCap,
  Building2,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Clock,
  CheckCircle
} from 'lucide-react';
import LoadingSpinner from './components/LoadingSpinner';
import logger from './utils/logger';

export default function AdminDashboard() {
  const { isArabic } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [userGrowth, setUserGrowth] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [topTeachers, setTopTeachers] = useState([]);

  // Load all dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  async function loadDashboardData() {
    try {
      setLoading(true);
      setError(null);

      // Get date range for analytics (last 30 days)
      const { startDate, endDate } = getDateRange(30);

      // Load all data in parallel
      const [statsData, growthData, revenueData, teachersData] = await Promise.all([
        getDashboardStats(),
        getUserGrowthData(startDate, endDate),
        getRevenueData(startDate, endDate, 'day'),
        getTopTeachers(5, 'bookings')
      ]);

      // Fetch ratings for all teachers
      const teachers = teachersData.top_teachers || [];
      if (teachers.length > 0) {
        const teacherEmails = teachers.map(t => t.email).filter(Boolean);
        const { data: ratingsData } = await getMultipleTeacherRatings(teacherEmails);

        // Merge ratings into teacher data
        const teachersWithRatings = teachers.map(teacher => ({
          ...teacher,
          avg_rating: ratingsData?.[teacher.email]?.avg_rating || 0,
          ratings_count: ratingsData?.[teacher.email]?.ratings_count || 0
        }));

        setTopTeachers(teachersWithRatings);
      } else {
        setTopTeachers(teachers);
      }

      setStats(statsData);
      setUserGrowth(growthData.daily_signups || []);
      setRevenue(revenueData.revenue_by_period || []);
    } catch (err) {
      logger.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }

  // Calculate growth percentage
  function calculateGrowth(current, previous) {
    if (!previous) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  }

  // Format currency
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BHD'
    }).format(amount || 0);
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingSpinner size={80} />
          <p style={{ color: '#666', fontSize: 14, marginTop: 20 }}>
            {isArabic ? 'جاري تحميل لوحة التحكم...' : 'Loading dashboard...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        background: '#fee',
        border: '1px solid #fcc',
        borderRadius: 8,
        padding: 24,
        margin: '20px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <AlertCircle size={24} color="#c00" />
        <div>
          <h3 style={{ color: '#c00', margin: '0 0 4px 0', fontSize: 16, fontWeight: 600 }}>
            {isArabic ? 'خطأ في تحميل البيانات' : 'Error Loading Data'}
          </h3>
          <p style={{ color: '#666', margin: 0, fontSize: 14 }}>{error}</p>
          <button
            onClick={loadDashboardData}
            style={{
              marginTop: 12,
              padding: '8px 16px',
              background: '#FF7426',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {isArabic ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'نظرة عامة' : 'Dashboard Overview'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'ملخص شامل لمنصة تعالم التعليمية'
            : 'Comprehensive overview of Taealam platform'}
        </p>
      </div>

      {/* Stats Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 20,
        marginBottom: 32
      }}>
        {/* Total Students */}
        <StatCard
          icon={<Users size={24} color="#FF7426" />}
          title={isArabic ? 'إجمالي الطلاب' : 'Total Students'}
          value={stats?.total_students || 0}
          trend={calculateGrowth(stats?.total_students, stats?.total_students - 10)}
          isArabic={isArabic}
        />

        {/* Total Teachers */}
        <StatCard
          icon={<GraduationCap size={24} color="#4CAF50" />}
          title={isArabic ? 'إجمالي المعلمين' : 'Total Teachers'}
          value={stats?.total_teachers || 0}
          trend={calculateGrowth(stats?.total_teachers, stats?.total_teachers - 5)}
          isArabic={isArabic}
        />

        {/* Total Institutes */}
        <StatCard
          icon={<Building2 size={24} color="#2196F3" />}
          title={isArabic ? 'إجمالي المعاهد' : 'Total Institutes'}
          value={stats?.total_institutes || 0}
          isArabic={isArabic}
        />

        {/* Total Requests */}
        <StatCard
          icon={<Calendar size={24} color="#9C27B0" />}
          title={isArabic ? 'إجمالي الطلبات' : 'Total Requests'}
          value={stats?.total_bookings || 0}
          isArabic={isArabic}
        />

        {/* Pending Bookings */}
        <StatCard
          icon={<Clock size={24} color="#FF9800" />}
          title={isArabic ? 'الحجوزات المعلقة' : 'Pending Bookings'}
          value={stats?.pending_bookings || 0}
          highlight={stats?.pending_bookings > 0}
          isArabic={isArabic}
        />

        {/* Today's Accepted Sessions */}
        <StatCard
          icon={<CheckCircle size={24} color="#4CAF50" />}
          title={isArabic ? 'جلسات اليوم المقبولة' : "Today's Accepted Sessions"}
          value={stats?.today_bookings || 0}
          isArabic={isArabic}
        />

        {/* Total Revenue */}
        <StatCard
          icon={<DollarSign size={24} color="#FF7426" />}
          title={isArabic ? 'الإيرادات الإجمالية' : 'Total Revenue'}
          value={formatCurrency(stats?.total_revenue || 0)}
          trend={15.3}
          isArabic={isArabic}
        />

        {/* Total Profit */}
        <StatCard
          icon={<DollarSign size={24} color="#4CAF50" />}
          title={isArabic ? 'إجمالي الأرباح' : 'Total Profit'}
          value={formatCurrency(stats?.total_profit || 0)}
          isArabic={isArabic}
        />

        {/* Security Issues */}
        <StatCard
          icon={<AlertCircle size={24} color="#f44336" />}
          title={isArabic ? 'المشاكل الأمنية' : 'Security Issues'}
          value={stats?.recent_security_issues || 0}
          highlight={stats?.recent_security_issues > 0}
          isArabic={isArabic}
          isWarning={true}
        />
      </div>

      {/* Charts Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: 20,
        marginBottom: 32
      }}>
        {/* User Growth Chart */}
        <ChartCard
          title={isArabic ? 'نمو المستخدمين (آخر 30 يوم)' : 'User Growth (Last 30 Days)'}
          data={userGrowth}
          isArabic={isArabic}
          type="user-growth"
        />

        {/* Revenue Chart */}
        <ChartCard
          title={isArabic ? 'الإيرادات (آخر 30 يوم)' : 'Revenue (Last 30 Days)'}
          data={revenue}
          isArabic={isArabic}
          type="revenue"
        />
      </div>

      {/* Top Teachers Table */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: 24,
        marginBottom: 32
      }}>
        <h2 style={{
          fontSize: 20,
          fontWeight: 600,
          color: '#232B3E',
          margin: '0 0 20px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <TrendingUp size={24} color="#FF7426" />
          {isArabic ? 'أفضل المعلمين' : 'Top Performing Teachers'}
        </h2>

        {topTeachers.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={tableHeaderStyle}>{isArabic ? 'المعلم' : 'Teacher'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'البريد الإلكتروني' : 'Email'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'الحجوزات' : 'Bookings'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'الإيرادات' : 'Revenue'}</th>
                  <th style={tableHeaderStyle}>{isArabic ? 'التقييم' : 'Rating'}</th>
                </tr>
              </thead>
              <tbody>
                {topTeachers.map((teacher, index) => (
                  <tr key={teacher.teacher_id} style={{
                    borderBottom: '1px solid #f0f0f0',
                    background: index % 2 === 0 ? '#fafafa' : '#fff'
                  }}>
                    <td style={tableCellStyle}>{teacher.teacher_name}</td>
                    <td style={tableCellStyle}>{teacher.teacher_email}</td>
                    <td style={tableCellStyle}>{teacher.booking_count}</td>
                    <td style={tableCellStyle}>{formatCurrency(teacher.total_revenue)}</td>
                    <td style={tableCellStyle}>
                      <span style={{
                        background: '#4CAF50',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 14,
                        fontWeight: 500
                      }}>
                        {teacher.avg_rating ? Number(teacher.avg_rating).toFixed(1) : 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px 0' }}>
            {isArabic ? 'لا توجد بيانات متاحة' : 'No data available'}
          </p>
        )}
      </div>
    </div>
  );
}

// StatCard Component
function StatCard({ icon, title, value, trend, highlight, isWarning, isArabic }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      padding: 20,
      border: highlight ? `2px solid ${isWarning ? '#f44336' : '#FF7426'}` : 'none',
      transition: 'transform 0.2s, box-shadow 0.2s',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-4px)';
      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{
          background: 'rgba(255, 116, 38, 0.1)',
          borderRadius: 8,
          padding: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {icon}
        </div>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {trend >= 0 ? (
              <TrendingUp size={16} color="#4CAF50" />
            ) : (
              <TrendingDown size={16} color="#f44336" />
            )}
            <span style={{
              fontSize: 14,
              fontWeight: 600,
              color: trend >= 0 ? '#4CAF50' : '#f44336'
            }}>
              {Math.abs(trend)}%
            </span>
          </div>
        )}
      </div>
      <p style={{ color: '#666', margin: '0 0 8px 0', fontSize: 14 }}>{title}</p>
      <h3 style={{ fontSize: 28, fontWeight: 700, color: '#232B3E', margin: 0 }}>
        {value}
      </h3>
    </div>
  );
}

// ChartCard Component
function ChartCard({ title, data, isArabic, type }) {
  // Calculate max value for scaling
  const maxValue = Math.max(
    ...data.map(d => type === 'user-growth' ? d.total_count : d.revenue),
    1
  );

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BHD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate total revenue for legend
  const totalRevenue = type === 'revenue' ? data.reduce((sum, d) => sum + Number(d.revenue || 0), 0) : 0;

  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      padding: 24
    }}>
      <h2 style={{
        fontSize: 18,
        fontWeight: 600,
        color: '#232B3E',
        margin: '0 0 20px 0'
      }}>
        {title}
      </h2>

      {data.length > 0 ? (
        <div>
          {/* Y-axis max value label */}
          <div style={{ marginBottom: 4, paddingLeft: 40, fontSize: 11, color: '#999' }}>
            {type === 'revenue' ? formatCurrency(maxValue) : maxValue}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {/* Y-axis line */}
            <div style={{
              width: 1,
              height: 200,
              background: '#e0e0e0',
              position: 'relative'
            }}>
              {/* Y-axis mid value */}
              <div style={{
                position: 'absolute',
                top: '50%',
                right: 4,
                fontSize: 10,
                color: '#999',
                transform: 'translateY(-50%)'
              }}>
                {type === 'revenue' ? formatCurrency(maxValue / 2) : Math.round(maxValue / 2)}
              </div>
            </div>

            {/* Chart area */}
            <div style={{ flex: 1 }}>
              <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 4 }}>
                {data.map((item, index) => {
                  const value = type === 'user-growth' ? item.total_count : Number(item.revenue);
                  const height = (value / maxValue) * 100;
                  const dateLabel = item.signup_date || item.period_start;
                  const displayValue = type === 'revenue' ? formatCurrency(value) : value;

                  return (
                    <div
                      key={index}
                      style={{
                        flex: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        position: 'relative'
                      }}
                      title={`${formatDate(dateLabel)}: ${displayValue}`}
                    >
                      <div style={{
                        height: `${height}%`,
                        background: type === 'user-growth'
                          ? 'linear-gradient(180deg, #FF7426 0%, #FF9800 100%)'
                          : 'linear-gradient(180deg, #4CAF50 0%, #8BC34A 100%)',
                        borderRadius: '4px 4px 0 0',
                        transition: 'all 0.3s',
                        cursor: 'pointer',
                        minHeight: 4,
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                        e.currentTarget.style.transform = 'scaleY(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.transform = 'scaleY(1)';
                      }}>
                        {/* Show value on top of tall bars */}
                        {height > 30 && (
                          <div style={{
                            position: 'absolute',
                            top: -20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            fontSize: 9,
                            color: '#666',
                            whiteSpace: 'nowrap',
                            fontWeight: 500
                          }}>
                            {type === 'revenue' && value > 0 ? formatCurrency(value) : (value > 0 ? value : '')}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* X-axis */}
              <div style={{
                height: 1,
                background: '#e0e0e0',
                marginTop: 0
              }}></div>

              {/* X-axis labels - show first, middle, and last date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: '#999' }}>
                <span>{formatDate(data[0]?.signup_date || data[0]?.period_start)}</span>
                {data.length > 2 && (
                  <span>{formatDate(data[Math.floor(data.length / 2)]?.signup_date || data[Math.floor(data.length / 2)]?.period_start)}</span>
                )}
                <span>{formatDate(data[data.length - 1]?.signup_date || data[data.length - 1]?.period_start)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ color: '#999' }}>{isArabic ? 'لا توجد بيانات' : 'No data available'}</p>
        </div>
      )}

      {/* Legend */}
      {type === 'user-growth' && data.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', gap: 16, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, background: '#FF7426', borderRadius: 2 }}></div>
            <span style={{ color: '#666' }}>
              {isArabic ? 'طلاب' : 'Students'}: {data.reduce((sum, d) => sum + d.students_count, 0)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, background: '#FF9800', borderRadius: 2 }}></div>
            <span style={{ color: '#666' }}>
              {isArabic ? 'معلمين' : 'Teachers'}: {data.reduce((sum, d) => sum + d.teachers_count, 0)}
            </span>
          </div>
        </div>
      )}

      {/* Revenue Legend */}
      {type === 'revenue' && data.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', gap: 16, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 12, height: 12, background: '#4CAF50', borderRadius: 2 }}></div>
            <span style={{ color: '#666' }}>
              {isArabic ? 'إجمالي الإيرادات' : 'Total Revenue'}: {formatCurrency(totalRevenue)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#666' }}>
              {isArabic ? 'الحجوزات' : 'Bookings'}: {data.reduce((sum, d) => sum + Number(d.booking_count || 0), 0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Table styles
const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 14,
  fontWeight: 600,
  color: '#666'
};

const tableCellStyle = {
  padding: '12px 16px',
  fontSize: 14,
  color: '#232B3E'
};
